import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../Head';
import { Hero } from '../Hero';
import * as css from './Services.m.css';
import * as commonCss from '../../Common.m.css';
import * as connectButtonCss from '../ConnectButton.m.css';
import { ConnectStrip } from '../ConnectStrip';
import { Link } from '@dojo/framework/routing/Link';
import { Summary } from '../Summary';
import ClientResults from '../ClientResults';
const elevateIcon = require('../../assets/images/icons/elevate.svg');
const guideIcon = require('../../assets/images/icons/guide.svg');
const communicationIcon = require('../../assets/images/icons/communication.svg');
const knowledgeIcon = require('../../assets/images/icons/knowledge.svg');
const agileIcon = require('../../assets/images/icons/agile.svg');
const prototypeIcon = require('../../assets/images/icons/prototype.svg');
const enterpriseIcon = require('../../assets/images/icons/enterprise.svg');
const fullstackIcon = require('../../assets/images/icons/fullstack.svg');
const performanceIcon = require('../../assets/images/icons/performance.svg');
const qualityIcon = require('../../assets/images/icons/quality.svg');
const devopsIcon = require('../../assets/images/icons/devops.svg');
const approachIcon = require('../../assets/images/icons/approach.svg');
const technologyIcon = require('../../assets/images/icons/technology.svg');
const legacyIcon = require('../../assets/images/icons/legacy.svg');
const deliveryIcon = require('../../assets/images/icons/delivery.svg');
const completeImg = require('../../assets/images/services/complete.svg');
const holisticImg = require('../../assets/images/services/holistic.svg');
const agileImg = require('../../assets/images/services/agile.svg');
const platformsImg = require('../../assets/images/services/platforms.svg');
const consultingImg = require('../../assets/images/services/consulting.svg');

const factory = create();

export const Services = factory(function Services() {
	return (
		<div classes={css.root}>
			<Head
				title="Custom Web App Development for Enterprises | SitePen"
				description="SitePen provides web app development for businesses and enterprises. We develop new applications and update legacy applications. Contact us to learn more."
			/>
			<div classes={css.heroWrapper}>
				<div classes={commonCss.contentWrapper}>
					<Hero>
						{{
							title: 'Achieve Your Web App Development Goals',
							xlTitle: 'With Confidence'
						}}
					</Hero>
				</div>
			</div>
			<div classes={css.shadow}>
				<div classes={commonCss.contentWrapper}>
					<section classes={css.topSection}>
						<div classes={css.leading}>
							<h2 classes={css.topTitle}>
								Developing enterprise web applications requires a lot more than an
								office-full of developers waiting for instructions.
							</h2>
						</div>
						<div classes={css.trailing}>
							<p>You need an agile team who is constantly moving forward to:</p>
							<ul classes={css.iconList}>
								<li classes={css.item}>
									<img classes={css.img} src={elevateIcon} alt="elevate icon" />
									Elevate your technical capabilities
								</li>
								<li classes={css.item}>
									<img classes={css.img} src={guideIcon} alt="guide icon" />
									Guide significant decisions
								</li>
								<li classes={css.item}>
									<img
										classes={css.img}
										src={communicationIcon}
										alt="communication icon"
									/>
									Keep communication frequent and open
								</li>
								<li classes={css.item}>
									<img
										loading="lazy"
										classes={css.img}
										src={knowledgeIcon}
										alt="knowledge icon"
									/>
									Share knowledge about the right tech, tools, and standards
								</li>
							</ul>
							<p>
								All of this to establish a healthy velocity to get your app through
								to production as efficiently as possible.
							</p>
							<p>
								As{' '}
								<Link to="about">leaders in enterprise development services</Link>{' '}
								and with our position at the forefront of the modern web, we offer a
								partnership that will optimize your approach in all areas of
								development and instill confidence in meeting your company’s
								objectives.
							</p>
						</div>
					</section>
					<section classes={css.resultsSection}>
						<h3 classes={css.title}>YOU CAN EXPECT RESULTS</h3>
						<ClientResults />
					</section>
				</div>
			</div>
			<div classes={commonCss.smallerContentWrapper}>
				<section>
					<h2 classes={[commonCss.heading, commonCss.centered, css.heading]}>
						We Will Drive Your Success
					</h2>
					<Summary smaller>
						{{
							title: 'Complete Team',
							blurb: (
								<span>
									Receive trusted guidance from our{' '}
									<Link to="expertise">complete cross-functional team</Link> on
									all aspects of agile development: Strategy, planning, business
									analysis,{' '}
									<Link to="servicesEnterpriseArchitecture">
										enterprise architecture
									</Link>
									,{' '}
									<Link to="servicesSoftwareArchitecture">
										software architecture
									</Link>
									, design, development, and{' '}
									<Link to="servicesQualityAssurance">quality assurance</Link>.
								</span>
							),
							image: (
								<img
									classes={commonCss.sizedSummaryImage}
									loading="lazy"
									src={completeImg}
									alt="Illustration for Complete Team"
								/>
							)
						}}
					</Summary>
					<Summary smaller reverse>
						{{
							title: 'Holistic Approach',
							blurb: (
								<span>
									Rely on a <Link to="home">partner</Link> who won’t follow
									blindly but instead considers your big-picture goals in every
									task. We work with you to address your entire situation and
									produce exceptional problem-solving results.
								</span>
							),
							image: (
								<img
									classes={css.holisticImage}
									loading="lazy"
									src={holisticImg}
									alt="Illustration for Holistic Approach"
								/>
							)
						}}
					</Summary>
					<Summary smaller>
						{{
							title: 'Agile Methodologies',
							blurb: (
								<p>
									Conquer the complexities of building and scaling{' '}
									<Link to="servicesEnterpriseSoftwareDevelopment">
										enterprise-level applications
									</Link>{' '}
									with a lean, agile process that effectively delivers on all of
									your requirements.
								</p>
							),
							image: (
								<img
									classes={commonCss.sizedSummaryImage}
									loading="lazy"
									src={agileImg}
									alt="Illustration for Agile Methodologies"
								/>
							)
						}}
					</Summary>
				</section>
			</div>
			<ConnectStrip variant="expertise" />
			<div classes={commonCss.contentWrapper}>
				<section>
					<h2 classes={[commonCss.heading, commonCss.centered, css.heading]}>
						Services We Offer
					</h2>
					<Summary smaller>
						{{
							title: 'Modern Enterprise Web Development',
							blurb: (
								<virtual>
									<p>
										Build and evolve your application the right way. Whether we
										partner with your product team or take full ownership with
										our team, we'll bring the{' '}
										<Link to="servicesWebApplicationDevelopment">
											experience and expertise to rapidly develop your
											application
										</Link>
										.
									</p>
									<p>Build the right application with:</p>
									<ul classes={css.iconList}>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={agileIcon}
												alt="agile icon"
											/>{' '}
											Agile discovery, analysis, and planning
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={prototypeIcon}
												alt="prototype Icon"
											/>
											<Link to="servicesRapidPrototyping">
												Rapid prototyping and design cycles
											</Link>
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={enterpriseIcon}
												alt="enterprise Icon"
											/>
											Enterprise UI and UX design
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={fullstackIcon}
												alt="fullstack Icon"
											/>
											Full-stack Enterprise JavaScript
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={performanceIcon}
												alt="performance Icon"
											/>
											<Link to="servicesPerformanceOptimization">
												Performance audits and improvements
											</Link>
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={qualityIcon}
												alt="quality Icon"
											/>
											Quality assurance testing
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={devopsIcon}
												alt="devops Icon"
											/>
											<Link to="servicesEnterpriseDevOpsSolutions">
												DevOps implementation
											</Link>
										</li>
									</ul>
									<Link
										to="servicesWebApplicationDevelopment"
										classes={[connectButtonCss.root, connectButtonCss.cta]}
									>
										MORE ABOUT WEB DEVELOPMENT
									</Link>
								</virtual>
							),
							image: (
								<img
									classes={commonCss.responsiveImage}
									loading="lazy"
									src={platformsImg}
									alt="Illustration for App Development"
								/>
							)
						}}
					</Summary>
					<Summary smaller reverse>
						{{
							title: 'Strategic & Technical Consulting',
							blurb: (
								<virtual>
									<p>
										Keep up with the evolving web engineering landscape and
										quickly adapt to shifting business priorities. We help you
										become{' '}
										<Link to="servicesEnterpriseTechnologyStrategy">
											strategically positioned
										</Link>{' '}
										by defining, structuring, and building the organizational
										and technical capabilities you need for lean, rapid web
										application deployment.
									</p>
									<p>Achieve your strategic goals with:</p>
									<ul classes={css.iconList}>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={approachIcon}
												alt="approach icon"
											/>
											<span>
												Development approach, planning, and{' '}
												<Link to="servicesCrossPlatformDevelopment">
													implementation
												</Link>
											</span>
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={technologyIcon}
												alt="technology icon"
											/>
											<span>
												Architecture, <Link to="servicesUXDesign">UX</Link>,
												open source software, and technology expertise
											</span>
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={legacyIcon}
												alt="legacy icon"
											/>
											<span>
												<Link to="servicesSoftwareModernization">
													Legacy application
												</Link>{' '}
												maintainability and re-engineering
											</span>
										</li>
										<li classes={css.item}>
											<img
												loading="lazy"
												classes={css.img}
												src={deliveryIcon}
												alt="delivery icon"
											/>
											Engineering culture, healthy teams, and continuous value
											delivery
										</li>
									</ul>
									<Link
										to="servicesEnterpriseTechnologyStrategy"
										classes={[connectButtonCss.root, connectButtonCss.cta]}
									>
										MORE ABOUT TECHNICAL STRATEGY
									</Link>
								</virtual>
							),
							image: (
								<img
									src={consultingImg}
									classes={commonCss.responsiveImage}
									loading="lazy"
									alt="Illustration for Consulting"
								/>
							)
						}}
					</Summary>
				</section>
			</div>
			<ConnectStrip />
		</div>
	);
});

export default Services;
